import { useLayoutEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthMode } from "../../hooks/useAuthMode";
import GuestBanner, { GuestBannerState } from "./GuestBanner";

/**
 * GuestShellBanner is the wiring glue between the auth-state hook and the
 * presentational GuestBanner. App.tsx mounts a single instance above the
 * existing app bar; the banner is non-dismissible by design and only renders
 * when the caller is currently a guest.
 *
 * Usage stays a one-line mount — `<GuestShellBanner />` — so the App.tsx diff
 * stays minimal.
 */
const dayMs = 24 * 60 * 60 * 1000;

const stateFor = (daysLeft: number): GuestBannerState => {
  if (daysLeft <= 0) return "expire";
  if (daysLeft <= 2) return "warn";
  return "fresh";
};

const fmtDaysLabel = (daysLeft: number): string => {
  if (daysLeft <= 0) return "Ends today";
  if (daysLeft === 1) return "1 day left";
  return `${daysLeft} days left`;
};

const GuestShellBanner: React.FC = () => {
  const { mode, guest } = useAuthMode();
  const { loginWithRedirect } = useAuth0();

  const view = useMemo(() => {
    if (mode !== "guest" || !guest) return null;
    const daysLeft = Math.max(
      0,
      Math.ceil((guest.expiresAtMs - Date.now()) / dayMs)
    );
    const usedMB = Math.max(0, Math.round(guest.usedBytes / (1024 * 1024)));
    const totalMB = Math.max(
      1,
      Math.round(guest.quotaBytes / (1024 * 1024))
    );
    return { state: stateFor(daysLeft), daysLabel: fmtDaysLabel(daysLeft), usedMB, totalMB };
  }, [mode, guest]);

  if (!view) return null;

  const onSave = () => {
    loginWithRedirect({
      appState: { targetUrl: "/initial-setting" },
      authorizationParams: { screen_hint: "signup" },
    });
  };

  return (
    <GuestBanner
      state={view.state}
      daysLabel={view.daysLabel}
      usedMB={view.usedMB}
      totalMB={view.totalMB}
      onSave={onSave}
      saveLabel="Save your work"
    />
  );
};

export default GuestShellBanner;
